import { DialogProps } from "@toolpad/core/useDialogs";
import { useEffect, useState, useMemo, JSX, useContext, useRef } from "react";
import { Person, Email, Phone, Business, LocationOn, Work, Cake, Interests, Lock, Tune } from "@mui/icons-material";
import { useApiClient } from "../../ApiClientProvider";
import { IUserProfileResponse, IRoleScopedProfileFieldsResponse } from "../../interfaces/IUserProfile";
import { PublicEventService } from "../../services/publicEventService";
import { Grid, Box, Typography, Chip, Divider, Paper, Dialog, Skeleton, Avatar, Tooltip, IconButton, DialogActions, Button, Alert, alpha, useTheme } from "@mui/material";
import { ConnectionService } from "../../services/connectionService";
import { useSession } from "../../SessionContext";

interface ProfileDialogPayload {
    userId: string;
    eventId?: string;
    roleId?: string;
    hideConnect?: boolean;
    primaryColor?: string;
    secondaryColor?: string;
    isDarkMode?: boolean;
}

interface ProfileDialogProps extends DialogProps<ProfileDialogPayload, void> { }

const getFieldMeta = (primaryColor: string): { [key: string]: { label: string; icon: JSX.Element } } => ({
    aboutYourself: { label: "About", icon: <Person sx={{ color: primaryColor }} /> },
    interests: { label: "Interests", icon: <Interests sx={{ color: primaryColor }} /> },
    location: { label: "Location", icon: <LocationOn sx={{ color: primaryColor }} /> },
});

export default function ProfileDialog({ payload, open, onClose }: ProfileDialogProps) {
    const theme = useTheme();
    const { session } = useSession();
    const [profile, setProfile] = useState<IUserProfileResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const apiClient = useApiClient("applicationClient");
    const eventService = useMemo(() => new PublicEventService(apiClient), [apiClient]);
    const connectionService = useMemo(() => new ConnectionService(apiClient), [apiClient]);
    const [alertMsg, setAlertMsg] = useState<string | null>(null);
    const [alertSeverity, setAlertSeverity] = useState<"success" | "error">("success");
    const hideConnect = payload?.hideConnect === true;
    
    // Only use custom colors if explicitly provided in payload (for embed)
    const hasCustomColors = !!payload?.primaryColor && !!payload?.secondaryColor;
    const isDarkMode = payload?.isDarkMode ?? theme.palette.mode === 'dark';
    const primaryColor = hasCustomColors ? payload.primaryColor! : theme.palette.primary.main;
    const secondaryColor = hasCustomColors ? payload.secondaryColor! : theme.palette.secondary.main;
    
    const isSelf = session?.user?.id === profile?.id;
    const canConnect = profile?.allowConnections ?? true;
    const connectionStatus = profile?.connectionStatus ?? "none";

    const handleConnect = async () => {
        try {
            if (profile) {
                if (window.chatManager && payload.eventId) {
                    await window.chatManager.sendRequest(profile.id, payload.eventId);
                } else {
                    await connectionService.create(profile.id, payload.eventId);
                    setAlertMsg("Connection request sent!");
                    setAlertSeverity("success");
                }
                setProfile(prev => prev ? { ...prev, connectionStatus: "pending" } : prev);
            }
        } catch {
            setAlertMsg("Failed to send connection request.");
            setAlertSeverity("error");
        }
    };

    useEffect(() => {
        if (!open || !payload?.userId) return;
        setLoading(true);
        (async () => {
            try {
                let data;
                if (payload.eventId && payload.roleId) {
                    data = await eventService.getDelegateProfile(payload.userId, payload.eventId, payload.roleId);
                } else if (payload.eventId) {
                    data = await eventService.getAttendeeProfile(payload.userId, payload.eventId);
                } else {
                    const response = await apiClient.get(`/participant/people/${payload.userId}/profile`);
                    data = response.data;
                }
                setProfile(data);
            } catch {
                setProfile(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [open, payload, eventService, apiClient]);

    const ExpandableText = ({ text, initialLines = 4 }: { text: string; initialLines?: number }) => {
        const ref = useRef<HTMLDivElement | null>(null);
        const [isOverflow, setIsOverflow] = useState(false);
        const [expanded, setExpanded] = useState(false);

        useEffect(() => {
            const el = ref.current;
            if (!el) return;

            const check = () => {
                // With clamp applied, if scrollHeight > clientHeight then content is clipped.
                setIsOverflow(el.scrollHeight > el.clientHeight + 1);
            };

            // Run once after render (styles applied)
            check();

            const ro = new ResizeObserver(check);
            ro.observe(el);
            return () => ro.disconnect();
        }, [text, initialLines]);

        const previewSx = {
            width: "100%",
            whiteSpace: "pre-wrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: initialLines,
            WebkitBoxOrient: "vertical" as const,
        };

        const fullSx = {
            width: "100%",
            whiteSpace: "pre-wrap",
        };

        return (
            <Box sx={{ position: "relative", mt: 0.5 }}>
                <Typography
                    component="div"
                    ref={ref}
                    variant="body2"
                    sx={expanded ? fullSx : previewSx}
                    aria-expanded={expanded}
                >
                    {text}
                </Typography>

                {!expanded && isOverflow && (
                    // gradient indicator
                    <Box
                        aria-hidden
                        sx={{
                            pointerEvents: "none",
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 28,
                            height: 28,
                            background: isDarkMode 
                                ? "linear-gradient(180deg, rgba(26,26,26,0) 0%, rgba(26,26,26,0.9) 60%, rgba(26,26,26,1) 100%)"
                                : "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(250,250,250,0.9) 60%, rgba(250,250,250,1) 100%)",
                        }}
                    />
                )}

                {isOverflow && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                        <Button
                            size="small"
                            onClick={() => setExpanded(e => !e)}
                            aria-expanded={expanded}
                            aria-label={expanded ? "Show less" : "Show more"}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                color: primaryColor,
                                background: "transparent",
                                "&:hover": { background: "transparent", transform: "scale(1.02)" },
                            }}
                        >
                            {expanded ? "Show less" : "Show more"}
                        </Button>
                    </Box>
                )}
            </Box>
        );
    };

    // Helper to render a field with privacy
    const renderField = (key: keyof IUserProfileResponse, value: any) => {
        const fieldMeta = getFieldMeta(primaryColor);
        const meta = fieldMeta[key as string];

        // Consider empty values as not renderable
        const isEmptyString = typeof value === "string" && value.trim() === "";
        const isEmptyArray = Array.isArray(value) && value.length === 0;
        if (value == null || isEmptyString || isEmptyArray) return null;

        return (
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 1 }}>
                <Avatar
                    sx={{
                        width: 36,
                        height: 36,
                        bgcolor: "transparent",
                        border: "1px solid",
                        borderColor: primaryColor,
                        color: primaryColor,
                        boxShadow: 0,
                    }}
                    aria-hidden
                >
                    {meta?.icon}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                        {meta?.label}
                    </Typography>

                    {Array.isArray(value) ? (
                        <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {value.map((v: string, idx: number) => (
                                <Chip key={idx} label={v} size="small" sx={{ backgroundColor: primaryColor, color: "white" }} />
                            ))}
                        </Box>
                    ) : (
                        <ExpandableText text={String(value)} />
                    )}
                </Box>
            </Box>
        );
    };

    // Role fields rendering
    const renderRoleFields = (roleFields: IRoleScopedProfileFieldsResponse[] | undefined) => {
        if (!roleFields || roleFields.length === 0) return null;
        return (
            <Box sx={{ mt: 3 }}>
                {roleFields.map((role, idx) => (
                    <>
                        <Grid container spacing={2}>
                            {role.fields.map((field, fidx) =>
                                field.isPubliclyVisible ? (
                                    <Grid size={12} key={fidx}>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Tune sx={{ color: primaryColor }} />
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {field.label}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body1" sx={{ mt: 1 }}>
                                                {field.value || <Chip label="Not Provided" size="small" sx={{ backgroundColor: primaryColor, color: "white" }} />}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ) : (
                                    <Grid size={12} key={fidx}>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Tune sx={{ color: "grey.500" }} />
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {field.label}
                                                </Typography>
                                            </Box>
                                            <Chip label="Not Shared" color="default" size="small" sx={{ mt: 1, bgcolor: "grey.200" }} />
                                        </Box>
                                    </Grid>
                                )
                            )}
                        </Grid>
                    </>
                ))}
            </Box>
        );
    };

    const hasFirstName = !!profile?.firstname?.trim();
    const hasLastName = !!profile?.lastname?.trim();
    const showName = hasFirstName || hasLastName;

    function getProfessionalSummary(profile: IUserProfileResponse) {
        const jobTitle = profile.jobTitle;
        const company = profile.company;
        const industry = profile.industry;

        if (jobTitle && company && industry) {
            return `${jobTitle} at ${company} in ${industry}`;
        }
        if (jobTitle && company) {
            return `${jobTitle} at ${company}`;
        }
        if (company && industry) {
            return `Working at ${company} in ${industry}`;
        }
        if (jobTitle && industry) {
            return `${jobTitle} in ${industry}`;
        }
        if (jobTitle) {
            return jobTitle;
        }
        if (company) {
            return `Working at ${company}`;
        }
        if (industry) {
            return `Working in the ${industry} industry`;
        }
        return "";
    }

    return (
        <Dialog
            open={open}
            onClose={() => onClose()}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                    backgroundColor: hasCustomColors && isDarkMode ? "#1a1a1a" : undefined,
                },
            }}
            aria-labelledby="profile-dialog-title"
        >
            <Box sx={{ p: 3, pb: 0 }}>
                {loading ? (
                    <Box>
                        {/* Avatar + Name Section */}
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
                            <Skeleton variant="circular" width={96} height={96} sx={{ mb: 2 }} />
                            <Skeleton variant="text" width={160} height={36} sx={{ mx: "auto", mb: 1 }} />
                            <Skeleton variant="text" width={120} height={28} sx={{ mx: "auto", mb: 1 }} />
                            <Skeleton variant="text" width={100} height={24} sx={{ mx: "auto" }} />
                        </Box>
                        {/* Fields Section */}
                        <Paper
                            elevation={1}
                            sx={{
                                p: 3,
                                borderRadius: 8,
                                ...(hasCustomColors && isDarkMode ? { background: "#1a1a1a !important" } : {}),
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                mb: 2,
                            }}
                        >
                            <Grid container spacing={2}>
                                {[...Array(6)].map((_, idx) => (
                                    <Grid size={6} key={idx}>
                                        <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
                                        <Skeleton variant="rectangular" width="80%" height={24} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                        {/* Social Links Skeleton */}
                        <Box sx={{ mt: 3 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Skeleton variant="text" width={120} height={32} sx={{ mb: 2, mx: "auto" }} />
                            <Grid container spacing={2} justifyContent="center">
                                {[...Array(4)].map((_, idx) => (
                                    <Grid size={2} key={idx} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Skeleton variant="circular" width={40} height={40} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Box>
                ) : profile ? (
                    <Box sx={{ color: isDarkMode ? "#ffffff" : "inherit" }}>
                        {/* Avatar and Name Section */}
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
                            <Avatar
                                src={profile.imageUrl}
                                alt={`${profile.firstname} ${profile.lastname}`}
                                sx={{
                                    width: 96,
                                    height: 96,
                                    boxShadow: 2,
                                    border: `4px solid ${primaryColor}20`,
                                    bgcolor: primaryColor,
                                }}
                            />
                            <Box sx={{ mt: 2 }}>
                                {showName ? (
                                    <Typography variant="h5" sx={{ fontWeight: 700, textAlign: "center" }}>
                                        {hasFirstName ? profile.firstname : ""}
                                        {hasFirstName && hasLastName ? " " : ""}
                                        {hasLastName ? profile.lastname : ""}
                                    </Typography>
                                ) : (
                                    <Typography variant="h5" sx={{ fontWeight: 700, textAlign: "center", color: "text.secondary" }}>
                                        Private Member
                                    </Typography>
                                )}
                            </Box>
                            {/* Professional Summary */}
                            {getProfessionalSummary(profile) && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
                                    <Business sx={{ fontSize: 28, color: primaryColor }} aria-label="Company" />
                                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", fontWeight: 500 }}>
                                        {getProfessionalSummary(profile)}
                                    </Typography>
                                </Box>
                            )}
                            {profile.location && (
                                <Typography variant="body1" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
                                    <LocationOn sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }} />
                                    From {profile.location}
                                </Typography>
                            )}
                        </Box>
                        {/* Fields Section */}
                        <Grid container spacing={2}>
                            {/* About Yourself - full width */}
                            <Grid size={12} sx={{ mb: 2 }}>
                                {renderField("aboutYourself", profile.aboutYourself)}
                            </Grid>
                            {/* Interests - full width */}
                            <Grid size={12} sx={{ mb: 2 }}>
                                {renderField("interests", profile.interests)}
                            </Grid>
                        </Grid>
                        {renderRoleFields(profile.roleFields)}
                    </Box>
                ) : (
                    <Typography variant="body1" color="error" sx={{ mt: 4 }}>
                        Unable to load profile.
                    </Typography>
                )}
            </Box>
            <Divider sx={{ backgroundColor: isDarkMode ? "rgba(255,255,255,0.12)" : "inherit" }} />
            {alertMsg && (
                <Box sx={{ px: 3, pb: 1 }}>
                    <Alert
                        severity={alertSeverity}
                        sx={{
                            borderRadius: 2,
                            mb: 2,
                            fontWeight: 500,
                        }}
                        onClose={() => setAlertMsg(null)}
                        aria-live="polite"
                    >
                        {alertMsg}
                    </Alert>
                </Box>
            )}
            <DialogActions sx={{ px: 3, py: 2 }}>
                {loading && session && !hideConnect && (
                    <Skeleton
                        variant="rounded"
                        width={120}
                        height={36}
                        sx={{ mr: 2, borderRadius: "8px" }}
                    />
                )}
                {!loading && profile && session && !hideConnect && !isSelf && canConnect && (
                    <Button
                        onClick={connectionStatus === "none" ? handleConnect : undefined}
                        color={
                            connectionStatus === "connected"
                                ? "success"
                                : connectionStatus === "pending"
                                    ? "warning"
                                    : "primary"
                        }
                        variant={
                            connectionStatus === "none"
                                ? "contained"
                                : "outlined"
                        }
                        disabled={connectionStatus !== "none"}
                        sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            boxShadow: 1,
                            mr: 2,
                            minWidth: 120,
                            transition: "transform 0.2s, box-shadow 0.2s",
                            ...(!hasCustomColors && connectionStatus === "none" && {
                                backgroundColor: primaryColor,
                                color: "#fff",
                                "&:hover": {
                                    backgroundColor: primaryColor,
                                },
                            }),
                            ...(hasCustomColors && {
                                background:
                                    connectionStatus === "connected"
                                        ? `linear-gradient(90deg, ${alpha(primaryColor, 0.3)} 0%, ${alpha(primaryColor, 0.4)} 100%)`
                                        : connectionStatus === "pending"
                                            ? `linear-gradient(90deg, ${alpha(primaryColor, 0.2)} 0%, ${alpha(secondaryColor, 0.2)} 100%)`
                                            : `linear-gradient(90deg, ${alpha(primaryColor, 0.15)} 0%, ${alpha(secondaryColor, 0.15)} 100%)`,
                                color: primaryColor,
                                border: `1px solid ${alpha(primaryColor, 0.4)}`,
                            }),
                            "&:hover": {
                                transform: connectionStatus === "none" ? "scale(1.05)" : undefined,
                                boxShadow: connectionStatus === "none" ? 3 : undefined,
                            },
                        }}
                        aria-label={
                            connectionStatus === "connected"
                                ? "Connected"
                                : connectionStatus === "pending"
                                    ? "Pending"
                                    : "Connect"
                        }
                    >
                        {connectionStatus === "connected"
                            ? "Connected"
                            : connectionStatus === "pending"
                                ? "Pending"
                                : "Connect"}
                    </Button>
                )}
                <Button
                    onClick={() => onClose()}
                    variant="contained"
                    sx={{
                        backgroundColor: primaryColor,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 500,
                        boxShadow: 1,
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": {
                            backgroundColor: primaryColor,
                            transform: "scale(1.05)",
                            boxShadow: 3,
                        },
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}